<template>
  <div>
    <eg-dropdown menu-class="folder-actions-dropdown-menu" boundary="window">
      <template #button-content>
        <slot name="button-content">
          <eg-icon icon="more-options-vertical-dots-dots" />
        </slot>
      </template>

      <div class="folder-actions-dropdown items-wrapper">
        <!-- Top items -->
        <eg-b-dropdown-item
          v-for="item in dropdownItems"
          :key="item.key"
          :value="item"
          :disabled="item.disabled"
          @click.stop="handleFolderAction(item)"
        >
          <eg-icon :icon="item.icon" size="small" />
          <span class="text-truncate paragraph-s">{{ item.label }}</span>
        </eg-b-dropdown-item>

        <!-- Divider if there are bottom items -->
        <eg-b-dropdown-divider v-if="hasBottomItems" />

        <!-- Bottom items -->
        <eg-b-dropdown-item
          v-for="item in bottomItems"
          :key="item.key"
          :value="item"
          @click.stop="handleFolderAction(item)"
        >
          <eg-icon
            v-if="item.key === 'delete'"
            icon="delete"
            class="text-danger"
            size="small"
          />
          <eg-icon
            v-else-if="['integrationSettings', 'syncTasks'].includes(item.key)"
            :icon="item.icon"
            size="small"
          />
          <span
            :class="[
              'text-truncate',
              'paragraph-s',
              { 'text-danger': item.key === 'delete' },
            ]"
          >
            {{ item.label }}
          </span>
        </eg-b-dropdown-item>
      </div>
    </eg-dropdown>
  </div>
</template>

<script>
import EgDropdown from "@/components/global/eg_dropdown.vue";
import NewFolderModal from "@/components/global/modals/new_folder_modal.vue";
import RenameFolderModal from "@/components/global/modals/rename_folder_modal.vue";
import { onAddSubmissions } from "@/components/essay/essays_table_utils";
import { addFolder, deleteFolder, getUser } from "@/api";
import { capitalize } from "@/utils/capitalize";
import { getWorkspaceProfileIdKey, mergeUserData } from "@/utils/userUtils";
import { PLATFORMS } from "@/constants";
import CreateWorkspaceIntegrationModal from "@/views/dashboard/workspace-integration/modal/create-workspace-integration-modal.vue";
import isLMSMixin from "@/mixins/isLMS";
import { mapGetters } from "vuex";

export default {
  name: "FolderActionsDropdown",

  components: {
    EgDropdown,
  },

  mixins: [isLMSMixin],

  inject: ["workspacesProvider"],

  props: {
    folderId: {
      type: Number,
      default: null,
    },
    isWorkspaceActions: {
      type: Boolean,
      required: false,
      default: false,
    },
    workspaceKey: {
      type: String,
      required: true,
    },
  },

  computed: {
    ...mapGetters("config", ["features"]),
    folder() {
      return this.workspacesProvider.getFolderById(
        this.workspaceKey,
        this.folderId
      );
    },

    isFolder() {
      return !this.folder?.isAssignment;
    },

    isConnected() {
      switch (this.workspaceKey) {
        case PLATFORMS.GOOGLE:
          return this.$store.getters.isGoogleConnected;
        case PLATFORMS.CANVAS:
          return this.$store.getters.isCanvasConnected;
        case PLATFORMS.SCHOOLOGY:
          return this.$store.getters.isSchoologyConnected;
        default:
          return false;
      }
    },

    isNeedShowNewAssignment() {
      return (
        (this.folder.hierarchyLevel === 2 && this.folder.maxDepth === 3) ||
        (this.folder.hierarchyLevel === 1 && this.folder.maxDepth === 2)
      );
    },

    dropdownItems() {
      const items = [];

      if (!this.features.bulkImports && this.isWorkspaceActions) {
        items.push({
          label: this.$t("common.importFrom", {
            platform: capitalize(this.workspaceKey),
          }),
          icon: "plus-add-more-maximize",
          key: "importFolders",
        });

        if (!this.isConnected) {
          items.push({
            label: this.$t("common.reconnectAccount", {
              platform: capitalize(this.workspaceKey),
            }),
            icon: "users-group",
            key: "reconnectAccount",
          });
        }

        return items;
      }

      if (
        this.isFolder &&
        this.workspaceKey === "essaygrader" &&
        this.isNeedShowNewAssignment
      ) {
        items.push({
          label: this.$t("common.newAssignment"),
          icon: "file-document",
          key: "newAssignment",
        });
      }
      if (
        this.isFolder &&
        this.workspaceKey === "essaygrader" &&
        !this.isNeedShowNewAssignment
      ) {
        items.push({
          label: this.$t("common.newFolder", {
            entity: this.$t("common.folder"),
          }),
          icon: "add-folder",
          key: "newFolder",
        });
      }

      if (
        !this.features.bulkImports &&
        !this.isFolder &&
        this.folder?.hasGradingDetails
      ) {
        if (this.isLMS) {
          items.push({
            label: this.$t("common.importEssays"),
            icon: "file-download",
            key: "upload-essay",
            disabled: !this.isConnected,
          });

          if (!this.isConnected) {
            items.push({
              label: this.$t("common.reconnectAccount", {
                platform: capitalize(this.workspaceKey),
              }),
              icon: "users-group",
              key: "reconnectAccount",
            });
          }
        } else {
          items.push({
            label: this.$t("common.uploadEssay"),
            icon: "file-upload",
            key: "upload-essay",
          });
        }
      }

      if (!this.isFolder && !this.folder?.hasGradingDetails) {
        items.push({
          label: this.$t("common.startGrading"),
          icon: "file-document",
          key: "addGradingDetails",
        });
      }

      items.push({
        label: this.$t("common.rename"),
        icon: "pen-edit",
        key: "rename",
      });

      if (this.isFolder && !this.isLMS) {
        items.push({
          label: this.$t("common.duplicate"),
          icon: "duplicate-copy",
          key: "duplicate",
        });
      }

      return items;
    },

    bottomItems() {
      if (this.isWorkspaceActions) {
        if (!this.features.bulkImports) {
          return [
            {
              label: this.$t("common.integrationSettings"),
              icon: "setting-edit-filter-gear",
              key: "integrationSettings",
            },
          ];
        } else {
          return [
            {
              label: this.$t("common.syncTasks"),
              icon: "setting-edit-filter-gear",
              key: "syncTasks",
            },
          ];
        }
      }

      return [
        {
          label: this.$t("common.delete"),
          icon: "delete",
          key: "delete",
        },
      ];
    },

    hasBottomItems() {
      return this.bottomItems.length > 0;
    },
  },

  methods: {
    handleFolderAction(action) {
      switch (action.key) {
        case "reconnectAccount":
          this.createWorkspaceIntegration(this.workspaceKey);
          break;
        case "importFolders":
          if (!this.isConnected) {
            this.createWorkspaceIntegration(this.workspaceKey);

            return;
          }

          this.$router.push({
            name: "imports-assignments",
            params: {
              platformKey: this.workspaceKey,
              id: this.$store.state.user[
                getWorkspaceProfileIdKey(this.workspaceKey)
              ],
            },
          });
          break;
        case "integrationSettings":
          this.$router.push({
            name: "account",
            query: {
              tab: "integrations",
            },
          });
          break;
        case "syncTasks":
          this.$router.push({
            name: "account",
            query: {
              tab: "syncTasks",
            },
          });
          break;
        case "newFolder":
          this.$egModal.show("new-folder-modal", {
            component: NewFolderModal,
            props: {
              folderId: this.folderId,
              workspaceKey: this.workspaceKey,
              isFolder: true,
            },
          });
          break;
        case "newAssignment":
          this.$egModal.show("new-folder-modal", {
            component: NewFolderModal,
            props: {
              folderId: this.folderId,
              workspaceKey: this.workspaceKey,
              isFolder: false,
            },
          });
          break;
        case "addGradingDetails":
          this.$router.push({
            name: "workspace-essay-upload",
            params: {
              workspaceKey: this.workspaceKey,
              id: this.folderId,
            },
          });
          break;
        case "rename":
          this.$egModal.show("rename-folder-modal", {
            component: RenameFolderModal,
            props: {
              folderId: this.folderId,
              workspaceKey: this.workspaceKey,
            },
          });
          break;
        case "delete":
          this.$showDeleteConfirmationModal({
            title: "Delete Folder",
            action: this.deleteFolderAction,
          });
          break;
        case "duplicate":
          this.duplicateFolder();
          break;
        case "upload-essay":
          onAddSubmissions({
            workspaceKey: this.workspaceKey,
            folderId: this.folderId,
          }).then(() => {
            if (
              this.$route.name === "workspace" &&
              +this.$route.params.id === +this.folderId &&
              this.$route.params.workspaceKey === this.workspaceKey
            ) {
              this.$eventBus.$emit("update-essays-table", {
                folderId: this.folderId,
                workspaceKey: this.workspaceKey,
              });

              return;
            }

            this.$router.push({
              name: "workspace",
              params: {
                workspaceKey: this.workspaceKey,
                id: this.folderId,
              },
            });
          });
          break;

        default:
          this.$emit("select", action);
          break;
      }
    },

    async duplicateFolder() {
      function processNode(node) {
        delete node.id;
        delete node.createdAt;
        delete node.updatedAt;
        delete node.deletedAt;
        delete node.hierarchyLevel;
        delete node.platform;

        if (node.children && Array.isArray(node.children)) {
          node.children.forEach(processNode);
        }

        return node;
      }

      const clone = JSON.parse(JSON.stringify(this.folder));
      const newFolders = processNode(clone);

      try {
        const response = await addFolder({
          folders: [newFolders],
        });

        if (!response.data || response.data.length === 0) return;

        const newFolder = response.data[0];

        this.workspacesProvider.addFolderWhere(
          this.workspaceKey,
          (node) => +node.id === +newFolder.parentId,
          newFolder
        );

        this.$showToastSuccess("Folder duplicated successfully", {
          title: "Success",
        });
      } catch (error) {
        this.$showToastError(
          error?.response?.data?.error || "Failed to duplicate folder"
        );
      }
    },

    redirectAfterFolderDelete() {
      const redirectToFolder = (id) => {
        this.$router.push({
          name: "workspace",
          params: { workspaceKey: this.workspaceKey, id },
        });
      };

      const redirectToClosestSibling = () => {
        const sibling = this.workspacesProvider.getClosestSiblingById(
          this.workspaceKey,
          this.folderId
        );
        sibling ? redirectToFolder(sibling.id) : this.$router.push(`/`);
      };

      const isDeletedFolderSelected = +this.$route.params.id === +this.folderId;

      if (isDeletedFolderSelected && this.folder.parentId) {
        return redirectToFolder(this.folder.parentId);
      }

      const isSelectedFolderDescendant = this.workspacesProvider
        .getFolderAncestors(this.workspaceKey, this.$route.params.id)
        .some((folder) => folder.id === this.folderId);

      if (isDeletedFolderSelected || isSelectedFolderDescendant) {
        redirectToClosestSibling();
      }
    },

    async deleteFolderAction() {
      try {
        await deleteFolder(this.folderId);

        if (this.$route.name.startsWith("workspace")) {
          this.redirectAfterFolderDelete();
        }

        this.workspacesProvider.removeFolderById(
          this.workspaceKey,
          this.folderId
        );
      } catch (error) {
        this.$showToastError("Failed to delete folder", { title: "Error" });
      }
    },
    async updateUserData() {
      const { data } = await getUser("me");
      const userData = mergeUserData(this, data);

      await this.$store.dispatch("setUser", userData);
    },
    createWorkspaceIntegration(platformKey) {
      this.$egModal.show("create-workspace-integration-modal", {
        component: CreateWorkspaceIntegrationModal,
        props: {
          platformKey: platformKey,
        },
        on: {
          onSuccess: () => this.updateUserData(),
        },
      });
    },
  },
};
</script>
